import * as React from "react";

import clsx from "clsx";
import Image from "next/image";
import { FaStar, FaStarHalf } from "react-icons/fa6";

import defaultAvatar from "@/assets/avatar-woman.png";

export interface TestimonialProps {
  variant?: "stars" | "rating";
  stars?: number;
  name?: string;
  date?: string;
  imageURL?: string;
  className?: string;
  children?: React.ReactNode | string;
}

export const roundToNearestPointFive = (n: number) =>
  (Math.round(Number(n) * 2) / 2).toFixed(1);

export const starsNumberTostarsArray = (stars: number) =>
  Array.from(
    { length: Math.ceil(stars) },
    (_, index) => index === Math.ceil(stars) - 1 && Math.ceil(stars) !== stars
  );

const Testimonial: React.FC<TestimonialProps> = ({
  variant = "rating",
  stars = 5,
  name = "",
  date = "",
  imageURL,
  className = "",
  children,
}) => {
  stars = Number(stars);
  return (
    <div
      className={clsx(
        "flex flex-col gap-2 rounded-3xl border border-[#24A6B2] p-4 font-roboto",
        className
      )}
    >
      <div className="flex">
        <QuotationMark />
        <QuotationMark />
      </div>
      <div className="text-body-1-400 text-neutral-600">{children}</div>
      <div className="flex gap-2">
        <div className="flex items-center justify-center">
          <Image
            src={imageURL && imageURL.trim() !== "" ? imageURL : defaultAvatar}
            alt="Cartoon avatar of a woman"
            width={44}
            height={44}
            className="aspect-square rounded-full object-cover"
          />
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex justify-between">
            <p className="text-body-1-300 text-neutral-500">{name}</p>
            <div className="flex">
              <QuotationMark flip />
              <QuotationMark flip />
            </div>
          </div>
          <div className="flex justify-between">
            {variant === "rating" ? (
              <div className="flex items-center gap-1 text-xs">
                <FaStar className="text-primary-400" />
                <span className="font-light leading-5 text-[#534D4C]">
                  {roundToNearestPointFive(stars)}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-xs">
                {starsNumberTostarsArray(stars).map((isHalf, index) =>
                  isHalf ? (
                    <FaStarHalf
                      data-testid="half-star"
                      className="text-primary-400"
                      key={"stars " + name + " " + date + " " + index}
                    />
                  ) : (
                    <FaStar
                      data-testid="full-star"
                      className="text-primary-400"
                      key={"stars " + name + " " + date + " " + index}
                    />
                  )
                )}
              </div>
            )}
            <p className="text-body-2-300 text-neutral-400">{date}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
Testimonial.displayName = "Testimonial";

const QuotationMark = ({ flip = false }) => (
  <div
    className={clsx(
      "h-4 w-4 bg-gradient-to-b from-[#24A6B2] to-[#D7F0F3]",
      flip
        ? "[clipPath:polygon(100%_0%,0%_100%,100%_100%)]"
        : "[clipPath:polygon(0%_0%,100%_0%,0%_100%)]"
    )}
  />
);

export default Testimonial;
